/**
* @summary - Component that Provides Technical Info Tab
* @author Yuvasri S
* @since 2024-07-04
*/
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid, Box, Button, IconButton, Typography } from '@mui/material';
import TabContentLayout from './TabContentLayout';
import CustomTextField from '../commoncomponents/CustomTextField';
import { getAuthToken, getcompanyId, getCreatedBy, getFacilityId } from "../../utility/tokenUtility";
import { MasterGraph, ifacSecurityURL } from '../../utility/apiURLS';
import axios from "axios";
import { updateTechnicalInfo } from '../../redux/reducers/HospitalInformationReducer/formSlice';
import { BreadcrumbBar } from '../../assets/styled-components/breadcrumbBar';
import HomeIconButton from '../commoncomponents/HomeIconButton';
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { DefaultCard } from '../../assets/styled-components/card';
import { useNavigate } from "react-router";
import CustomSelectInput from '../commoncomponents/CustomSelectInput';
import CustomDialog from '../commoncomponents/CustomDialog';

function TechnicalInfoContent({ hospitalcode, hospitalname, technicalInfo, onUpdateTechnicalInfo, hospitalid, isViewMode, isEditMode }) {
    const dispatch = useDispatch();
    const { hospitalgroup, region, databasename, servername, companyname, clsvorate } = technicalInfo;
    const [hospitalGroupOptions, setHospitalGroupOptions] = useState([]);
    const hospitalDetailsId = useSelector((state) => state.form?.mashospitaldetailsId || '');
    const token = getAuthToken("id_token");
    const config = {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    };
    const facilityId = getFacilityId('facilityId');
    const createdBy = getCreatedBy('createdBy');
    const navigate = useNavigate();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [successDialogOpen, setSuccessDialogOpen] = useState(false);
    const [succesDialogMessage, setSuccessDialogMessage] = useState('');
    const [errors, setErrors] = useState({
        hospitalgroup: false, clsvorate: false
    });

    const [errorMessages, setErrorMessages] = useState({
        hospitalgroup: '', clsvorate: ''
    });
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);


    const fetchHospitalGroupOptions = async () => {
        try {
            const response = await axios.get(`${MasterGraph}/api/mashospitalgroups`, config);
            const hospitalGroupData = response.data;
            const options = hospitalGroupData.map(hospitalGroup => ({
                value: hospitalGroup.hospitalgroup,
                label: hospitalGroup.hospitalgroup,
                id: hospitalGroup.id
            }));
            setHospitalGroupOptions(options);
        } catch (error) {
            console.error("Error fetching hospital group options:", error);
        }
    };

    const fetchTechnicalInfo = async () => {
        try {
            // Fetch technical info from the first API
            const response1 = await axios.get(`${ifacSecurityURL}/api/GraphHospitalMaster/GetSecappdetailByFacilityid/${facilityId}`, config);
            const technicalInfo1 = response1.data.length ? response1.data[0] : {}; // Use first item or empty object if array is empty

            // Fetch technical info from the second API
            const response2 = await axios.get(`${MasterGraph}/api/GraphHospitalMaster/GetHospitalMasterByFacilityId/${hospitalid}`, config);
            const technicalInfo2 = response2.data.length ? response2.data[0] : {}; // Use first item or empty object if array is empty

            // Handle case where both responses are empty
            if (!technicalInfo1 && !technicalInfo2) {
                console.error("No Technical Info found");
                setDialogOpen(true);
                return; // Exit early if no data found
            }

            // Destructure hospital object to exclude the id field if technicalInfo2 is not empty
            const { id, ...technicalInfo2WithoutId } = technicalInfo2 || {};

            // Merge responses, prefer technicalInfo1 values if both are provided
            const mergedInfo = {
                ...technicalInfo2WithoutId, // Ensure technicalInfo2 values are used
                ...technicalInfo1 // Overwrite with technicalInfo1 values if they exist
            };

            // Validate additionalInfo and its properties
            let hospitalgroup = {};
            if (mergedInfo && mergedInfo.mashospitalgroup) {
                hospitalgroup = {
                    value: mergedInfo.mashospitalgroup.hospitalgroup || '',
                    id: mergedInfo.mashospitalgroup.id || '',
                    label: mergedInfo.mashospitalgroup.hospitalgroup || ''
                };
            }

            const technicalInfoToUpdate = {
                hospitalgroup: hospitalgroup || '',
                region: mergedInfo.regionname || '',
                databasename: mergedInfo.appdbname || '',
                servername: mergedInfo.appservername || '',
                companyname: mergedInfo.companyname || '',
                clsvorate: mergedInfo.clsvorate || ''
            };


            // Update state
            if (onUpdateTechnicalInfo) {
                onUpdateTechnicalInfo(technicalInfoToUpdate);
            }

            // Dispatch action
            if (technicalInfoToUpdate) {
                dispatch(updateTechnicalInfo(technicalInfoToUpdate));
            }

        } catch (error) {
            console.error("Error fetching Technical Info:", error);
        }
    };


    const handleChange = (field, newValue) => {
        onUpdateTechnicalInfo({ [field]: newValue });
        setHasUnsavedChanges(true); // Flagging that there are unsaved changes
        switch (field) {
            case 'hospitalgroup':
                dispatch(updateTechnicalInfo({ hospitalgroup: newValue }));
                if (newValue && newValue.id) {
                    setErrors((prevErrors) => ({ ...prevErrors, hospitalgroup: false }));
                    setErrorMessages((prevMessages) => ({ ...prevMessages, hospitalgroup: '' }));
                } else {
                    setErrors((prevErrors) => ({ ...prevErrors, hospitalgroup: true }));
                    setErrorMessages((prevMessages) => ({ ...prevMessages, hospitalgroup: 'Hospital Group is required' }));
                }
                break;

            case 'region':
                dispatch(updateTechnicalInfo({ region: newValue }));
                break;
            case 'databasename':
                dispatch(updateTechnicalInfo({ databasename: newValue }));
                break;
            case 'servername':
                dispatch(updateTechnicalInfo({ servername: newValue }));
                break;
            case 'companyname':
                dispatch(updateTechnicalInfo({ companyname: newValue }));
                break;
            case 'clsvorate':
                dispatch(updateTechnicalInfo({ clsvorate: newValue }));
                if (/^\d*\.?\d*$/.test(newValue)) {
                    setErrors((prevErrors) => ({ ...prevErrors, clsvorate: false }));
                    setErrorMessages((prevMessages) => ({ ...prevMessages, clsvorate: '' }));
                } else {
                    setErrors((prevErrors) => ({ ...prevErrors, clsvorate: true }));
                    setErrorMessages((prevMessages) => ({ ...prevMessages, clsvorate: 'Please enter a valid number' }));
                }
                break;
            default:
                break;
        }
    };
    const handleSubmit = (e) => {
        e.preventDefault();

        let formIsValid = true;
        const newErrors = { hospitalgroup: false, clsvorate: false };
        const newErrorMessages = { hospitalgroup: '', clsvorate: '' };

        // Validate hospitalgroup
        if (!hospitalgroup || !hospitalgroup.id) { // Ensure you're checking the state value
            formIsValid = false;
            newErrors.hospitalgroup = true;
            newErrorMessages.hospitalgroup = 'Hospital Group is required';
        }
        // Validate clsvorate
        if (!/^\d*\.?\d*$/.test(clsvorate)) {
            formIsValid = false;
            newErrors.clsvorate = true;
            newErrorMessages.clsvorate = 'Please enter a valid number';
        }
        setErrors(newErrors);
        setErrorMessages(newErrorMessages);

        if (formIsValid) {
            handleSave();
            setHasUnsavedChanges(false);
        }
    };
    const handleSave = async () => {
        try {
            const payload = {
                createdby: createdBy,
                facilityid: hospitalid,
                MashospitalgroupId: hospitalgroup.id,
                clsvorate: clsvorate,
                id: hospitalDetailsId
            };

            // PUT request
            const putUrl = `${MasterGraph}/api/GraphHospitalMaster/UpdateHospitalTechinfo/${hospitalDetailsId}`;
            await axios.put(putUrl, payload, config);
            setSuccessDialogMessage('Data updated successfully.');
            // Update state or dispatch actions as needed
            dispatch(updateTechnicalInfo({
                hospitalgroup,
                clsvorate
            }));

            // Additional state updates if required
            // ...
            setSuccessDialogOpen(true);
        } catch (error) {
            console.error("Error updating technical info:", error);
        }
    };


    const handleCancel = () => {
        if (hasUnsavedChanges && !isViewMode) {
            setCancelDialogOpen(true);
        } else {
            navigate(-1, { replace: true });
        }
    };

    const handleCancelDialogClose = () => {
        setCancelDialogOpen(false);
    };

    const handleCancelDialogConfirm = () => {
        setCancelDialogOpen(false);
        navigate(-1, { replace: true });
    };

    useEffect(() => {
        fetchHospitalGroupOptions();
        fetchTechnicalInfo();
    }, [hospitalcode]);

    const handleDialogClose = () => {
        setDialogOpen(false);
    };

    const handleDialogConfirm = () => {
        setDialogOpen(false);
    };


    return (
        <TabContentLayout>

            <DefaultCard sx={{ height: '100%' }}>
                <form onSubmit={handleSubmit} style={{ position: 'relative' }}>
                    <Box mb={2}>
                        <Grid container spacing={2}>
                            <Grid item xs={6} sm={4}>
                                <CustomTextField
                                    label="Hospital Code"
                                    id="hospital-code"
                                    value={hospitalcode}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <CustomTextField
                                    maxlength={225}
                                    label="Hospital Name"
                                    id="hospital-name"
                                    value={hospitalname}
                                    disabled
                                />
                            </Grid>

                            <Grid item xs={6} sm={4}>
                                <CustomSelectInput
                                    maxlength={10}
                                    id="hospital-group"
                                    label="Hospital Group"
                                    value={hospitalgroup}
                                    options={hospitalGroupOptions}
                                    getOptionLabel={(option) => option.label}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    onChange={(newValue) => handleChange('hospitalgroup', newValue)}
                                    disabled={isViewMode}
                                    isMandatoryField={true}
                                    error={errors.hospitalgroup}
                                    errorMessage={errorMessages.hospitalgroup}
                                    autoFocus
                                />
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <CustomTextField
                                    maxlength={40}
                                    label="Region"
                                    id="region"
                                    value={region}
                                    onChange={(newValue) => handleChange('region', newValue)}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <CustomTextField
                                    maxlength={30}
                                    label="Database Name"
                                    id="database-name"
                                    value={databasename}
                                    onChange={(newValue) => handleChange('databasename', newValue)}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <CustomTextField
                                    maxlength={30}
                                    label="Server Name"
                                    id="server-name"
                                    value={servername}
                                    onChange={(newValue) => handleChange('servername', newValue)}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <CustomTextField
                                    maxlength={40}
                                    label="Company Name"
                                    id="company-name"
                                    value={companyname}
                                    onChange={(newValue) => handleChange('companyname', newValue)}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <CustomTextField
                                    maxlength={9}
                                    label="CLS VO Rate"
                                    id="clsvorate"
                                    value={clsvorate}
                                    onChange={(newValue) => handleChange('clsvorate', newValue)}
                                    disabled={isViewMode}
                                    error={errors.clsvorate}
                                    errorMessage={errorMessages.clsvorate}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </form>
            </DefaultCard>
            <CustomDialog
                open={dialogOpen}
                onClose={handleDialogClose}
                title="Error"
                content="No Technical Info found"
                confirmBtn="OK"
                onConfirm={handleDialogConfirm}
            />
            <CustomDialog
                open={successDialogOpen}
                onClose={() => setSuccessDialogOpen(false)}
                content={succesDialogMessage}
                confirmBtn="OK"
                onConfirm={() => setSuccessDialogOpen(false)}
            />
            <CustomDialog
                open={cancelDialogOpen}
                onClose={handleCancelDialogClose}
                title="Unsaved Changes"
                content="Record(s) not Saved/Updated. Do you want to continue?"
                confirmBtn="Yes"
                cancelBtn="No"
                onConfirm={handleCancelDialogConfirm}
                onCancel={handleCancelDialogClose}
            />
            {isViewMode ? (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button variant="outlined" sx={{ m: 1 }} onClick={handleCancel}>
                        Back
                    </Button>
                </Box>

            ) : (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button variant="contained" color="primary" sx={{ m: 1 }} onClick={handleSubmit}>
                        Save
                    </Button>
                    <Button variant="outlined" sx={{ m: 1 }} onClick={handleCancel}>
                        Back
                    </Button>
                </Box>
            )}
        </TabContentLayout>
    );
}

export default TechnicalInfoContent;
